import { useState, useCallback } from 'react';

/**
 * 高度フィルタ状態管理フック
 *
 * @param {Object} options
 * @param {Object} options.initialValues - 初期値 { key: string[] | string }
 * @param {string} options.initialMode  - 'AND' | 'OR'
 */
export default function useAdvancedFilter({ initialValues = {}, initialMode = 'AND' } = {}) {
  const [filterValues, setFilterValues] = useState(initialValues);
  const [filterMode, setFilterMode] = useState(initialMode);

  const handleFieldChange = useCallback((key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilterValues((prev) => {
      const cleared = {};
      Object.keys(prev).forEach((key) => {
        cleared[key] = Array.isArray(prev[key]) ? [] : '';
      });
      return cleared;
    });
  }, []);

  /**
   * フィルタ述語関数を構築
   *
   * @param {Object} fieldMatchers - { key: (row, filterValue) => boolean }
   * @returns {(row: Object) => boolean}
   */
  const buildFilterPredicate = useCallback(
    (fieldMatchers) => {
      return (row) => {
        const activeFilters = Object.entries(filterValues).filter(([, val]) => {
          if (Array.isArray(val)) return val.length > 0;
          return val !== '' && val !== undefined && val !== null;
        });

        if (activeFilters.length === 0) return true;

        const results = activeFilters.map(([key, val]) => {
          const matcher = fieldMatchers[key];
          if (!matcher) return true;
          return matcher(row, val);
        });

        return filterMode === 'AND'
          ? results.every(Boolean)
          : results.some(Boolean);
      };
    },
    [filterValues, filterMode],
  );

  return {
    filterValues,
    filterMode,
    setFilterMode,
    handleFieldChange,
    clearFilters,
    buildFilterPredicate,
  };
}
